import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography } from "@mui/material";
import { ArrowBackIosNew } from "@mui/icons-material";
import tokens from "../../../shared/theme/tokens";

/**
 * Page header for authorized screens inside the AppShell. Replaces the
 * ~20-prop legacy Header for in-shell pages: title, optional back
 * navigation, optional right-side actions.
 */
export const PageHeader = ({
  title,
  subtitle,
  onBack,
  actions,
  sx,
}: {
  title: string;
  subtitle?: string;
  onBack?: (() => void) | true;
  actions?: React.ReactNode;
  sx?: object;
}) => {
  const navigate = useNavigate();
  const handleBack = onBack === true ? () => navigate(-1) : onBack;

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={{ mb: 2.5, ...sx }}
    >
      {handleBack && (
        <Box
          component="button"
          type="button"
          aria-label="Back"
          onClick={handleBack}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            border: `1px solid ${tokens.colors.border}`,
            borderRadius: `${tokens.radius.sm}px`,
            backgroundColor: tokens.colors.surface,
            color: tokens.colors.inkSecondary,
            cursor: "pointer",
            "&:hover": { backgroundColor: tokens.colors.hover },
          }}
        >
          <ArrowBackIosNew sx={{ fontSize: 14 }} />
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: tokens.fonts.display,
            fontSize: { xs: 18, md: 20 },
            fontWeight: 600,
            letterSpacing: "-0.015em",
            color: tokens.colors.ink,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{ fontSize: 13, color: tokens.colors.inkSecondary, mt: 0.25 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
    </Stack>
  );
};
